"use client";

import {
  Button,
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  IconButton,
} from "@dafiti/closet-ui";
import { Close, Sorting } from "@dafiti/closet-ui/icons";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { useSortControl } from "../../../hooks/useSortControl.hook";
import type { SortControlProps } from "../../../types/sortControl.type";
import { SortOptionList } from "./SortOptionList";

export function SortControlMobile({ sorting }: SortControlProps) {
  const t = useTranslations("catalog");
  const { currentSort, isNonDefaultSort, applySort } = useSortControl(sorting);
  const [drawerSelection, setDrawerSelection] = useState("");

  const handleDrawerOpen = () => {
    setDrawerSelection(currentSort);
  };

  const handleDrawerConfirm = () => {
    applySort(drawerSelection || currentSort);
  };

  return (
    <Drawer>
      <DrawerTrigger asChild>
        <IconButton
          variant={isNonDefaultSort ? "primary" : "secondary"}
          size="md"
          onClick={handleDrawerOpen}
          aria-label={t("sort.sort")}
          className="flex lg:hidden"
          data-testid="sort-control-mobile-trigger"
        >
          <div className="w-6 h-6 flex items-center justify-center">
            <Sorting className="size-3.5!" />
          </div>
        </IconButton>
      </DrawerTrigger>

      <DrawerContent
        aria-describedby={undefined}
        className="pb-0"
        data-testid="sort-control-mobile-drawer"
      >
        <DrawerHeader
          className="flex items-center justify-between"
          data-testid="sort-drawer-header"
        >
          <DrawerTitle className="text-heading-5 font-bold text-gray-600">
            {t("sort.sort")}
          </DrawerTitle>

          <DrawerClose asChild>
            <IconButton
              variant="subtle"
              size="md"
              aria-label={t("sort.closeSort")}
              data-testid="sort-drawer-close"
            >
              <Close />
            </IconButton>
          </DrawerClose>
        </DrawerHeader>

        <DrawerBody className="mb-0 pb-0">
          <SortOptionList
            options={sorting}
            currentValue={drawerSelection || currentSort}
            onSelect={setDrawerSelection}
          />
        </DrawerBody>

        <DrawerFooter className="py-6">
          <DrawerClose asChild>
            <Button
              variant="primary"
              size="lg"
              onClick={handleDrawerConfirm}
              aria-label={t("sort.confirmSort")}
              data-testid="sort-drawer-confirm"
              className="w-full flex items-center justify-center gap-2"
            >
              <Sorting className="text-white size-4" />
              <span className="text-xs font-normal">{t("sort.sort")}</span>
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
